/*
 * Copyright 2026 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 */

import i18n.StringKey
import i18n.Strings
import i18n.tr
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.GlobalScope
import kotlinx.coroutines.launch
import kotlinx.datetime.Clock
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.plus
import kotlinx.datetime.toLocalDateTime
import models.GoalAccountType
import models.GoalCategory
import models.SavingsGoal
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.HTMLSelectElement
import studio.Recommendation
import studio.StudioOrchestrator
import studio.VehicleEventType
import studio.VehicleProjection
import studio.VehicleType
import studio.Wiz3rUserProfile

/**
 * Goal Allocation Studio screen.
 *
 * The Studio is the single edit surface for a goal — name, category,
 * target, deadline, account type, monthly contribution, expected return,
 * and future marginal rate all sit here with vehicle comparisons + a
 * growth chart underneath. Edits stay uncommitted until the user clicks
 * Save in the footer; Cancel restores the last-saved snapshot.
 */
class WebStudioComponent(
    private val goal: SavingsGoal,
    private val profile: Wiz3rUserProfile,
    private val viewModel: WebSavingsGoalsViewModel,
    private val onPinned: (VehicleType) -> Unit,
    private val onDismiss: () -> Unit,
    private val isCreating: Boolean = false,
    // When set, the Lab is exploring an existing goal: the footer becomes an
    // explicit "Apply to goal" that lists which fields change before committing
    // (rather than a silent Save). Null → normal Save / create behavior.
    private val labApplyToGoal: SavingsGoal? = null,
) {
    private val today: LocalDate = Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()).date

    // -----------------------------------------------------------------------
    // SAVED snapshot — what's persisted right now
    // -----------------------------------------------------------------------
    private var savedGoal: SavingsGoal = goal
    private var savedSliders: StudioOrchestrator.SliderState =
        StudioOrchestrator.defaultSliders(goal, profile, today)

    // -----------------------------------------------------------------------
    // WORKING state — user's uncommitted edits
    // -----------------------------------------------------------------------
    private var workingName: String = goal.name
    private var workingCategory: GoalCategory = goal.category
    private var workingTargetAmount: Double = goal.targetAmount
    private var workingDeadline: LocalDate = goal.deadline
    private var workingPinned: VehicleType? = goal.accountType
    private var workingPriority: models.GoalPriority = goal.priority
    // Goal display icon as a CategoryIconPaths icon ID (never a raw emoji).
    // Seeds from the goal's stored icon (resolving legacy emoji) and falls back
    // to the category's default icon so a fresh draft already looks right.
    private var workingIcon: String = iconIdFor(goal.iconEmoji, goal.category)
    private var sliders: StudioOrchestrator.SliderState = savedSliders

    /** Resolve a goal's stored icon value (id or legacy emoji) to a valid icon
     *  ID, falling back to the category's default when unknown/blank. */
    private fun iconIdFor(stored: String?, cat: GoalCategory): String =
        utils.CategoryIconPaths.resolveIconId(stored ?: "")
            ?: utils.CategoryIconPaths.goalCategoryIconId[cat.name]
            ?: "target_arrow"

    // Y-axis animation state — see the chart-render comments for the full
    // rationale on hysteresis + smoothing.
    private var targetAxisMax: Double = 0.0
    private var animatedAxisMax: Double = 0.0
    private var animationActive: Boolean = false
    private var backdrop: HTMLElement? = null
    private var currentPanel: HTMLElement? = null

    fun show() {
        val tm = WebTheme.current
        val bd = (document.createElement("div") as HTMLElement).apply {
            style.cssText = """
                position: fixed; inset: 0; background: rgba(0,0,0,0.6);
                z-index: 1200; display: flex; align-items: flex-start; justify-content: center;
                overflow-y: auto; padding: 24px 16px;
            """.trimIndent()
        }
        val panel = (document.createElement("div") as HTMLElement).apply {
            style.cssText = """
                background: ${tm.card}; color: ${tm.textPrimary};
                border-radius: 16px; width: 100%; max-width: 900px;
                padding: 24px; box-shadow: 0 24px 64px rgba(0,0,0,0.25);
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            """.trimIndent()
        }
        bd.appendChild(panel)
        document.body?.appendChild(bd)
        bd.addEventListener("click", { e -> if (e.target == bd) close() })
        panel.addEventListener("click", { it.stopPropagation() })
        backdrop = bd
        currentPanel = panel
        renderInto(panel)
    }

    private fun close() {
        backdrop?.remove()
        backdrop = null
        onDismiss()
    }

    /** Build a transient SavingsGoal from the working state — passed to
     *  StudioOrchestrator so projections reflect uncommitted edits. */
    private fun workingGoal(): SavingsGoal = savedGoal.copy(
        name = workingName,
        category = workingCategory,
        targetAmount = workingTargetAmount,
        deadline = workingDeadline,
        accountType = workingPinned,
        priority = workingPriority,
        iconEmoji = workingIcon,
    )

    private fun isDirty(): Boolean {
        return workingName != savedGoal.name ||
            workingCategory != savedGoal.category ||
            workingTargetAmount != savedGoal.targetAmount ||
            workingDeadline != savedGoal.deadline ||
            workingPinned != savedGoal.accountType ||
            workingPriority != savedGoal.priority ||
            workingIcon != iconIdFor(savedGoal.iconEmoji, savedGoal.category) ||
            sliders != savedSliders
    }

    private fun renderInto(panel: HTMLElement) {
        val tm = WebTheme.current
        // data-guide anchors on each section host so the in-modal 'i' tour
        // (GuideScreen.STUDIO) can highlight them in turn.
        panel.innerHTML = """
            <div id="studioHeader" data-guide="studio-header"></div>
            <div id="studioProgress" data-guide="studio-progress" style="margin-top:10px;"></div>
            <div id="studioStrip" data-guide="studio-strip" style="display:flex; gap:12px; overflow-x:auto; padding-bottom:8px; margin-top:16px;"></div>
            <div id="studioForm" data-guide="studio-form" style="margin-top:14px;"></div>
            <div id="studioChart" data-guide="studio-chart" style="margin-top:18px;"></div>
            <div id="studioSavingsRate" data-guide="studio-savings-rate" style="margin-top:20px;"></div>
            <div id="studioFooter" data-guide="studio-footer" style="margin-top:18px;"></div>
            <div style="
                margin-top:18px; padding-top:12px;
                border-top:1px solid ${tm.border};
                color:${tm.textSecondary}; font-size:11px; line-height:1.5;
            ">${StringKey.STUDIO_DISCLAIMER.tr()}</div>
        """.trimIndent()
        renderAll(panel)
    }

    /** Re-render every section so the (unsaved) chip, vehicle strip,
     *  chart, savings-rate, progress bar, and footer all reflect the
     *  current working state. Used after every working-state mutation
     *  and after Save / Cancel. */
    private fun renderAll(panel: HTMLElement) {
        renderHeader(panel)
        renderGoalForm(panel)
        renderStrip(panel)
        renderChart(panel)
        renderSavingsRate(panel)
        renderProgress(panel)
        renderFooter(panel)
    }

    // -----------------------------------------------------------------------
    // Header (title + unsaved chip + edit profile + close)
    // -----------------------------------------------------------------------

    private fun renderHeader(panel: HTMLElement) {
        val tm = WebTheme.current
        val host = panel.querySelector("#studioHeader") as? HTMLElement ?: return
        // Pill chip — soft amber tint + bright amber text + matching border.
        // The previous treatment used a dark-amber-on-darker-amber combo that
        // disappeared into the dark sheet background. Now: amber-only palette
        // with high contrast against the dark sheet, but not so loud that it
        // competes with the heading.
        val unsavedChip = if (isDirty()) """
            <span style="
                display:inline-flex; align-items:center; gap:5px;
                padding:3px 9px; border-radius:999px;
                background:rgba(245,166,35,0.16); color:#FFB84D;
                border:1px solid rgba(245,166,35,0.45);
                font-size:11px; font-weight:600; letter-spacing:0.2px;
                margin-left:10px; vertical-align:middle; line-height:1.2;
                text-transform:lowercase;
            ">
                <span style="width:6px; height:6px; border-radius:50%; background:#FFB84D;"></span>
                ${StringKey.STUDIO_UNSAVED_CHIP.tr()}
            </span>
        """.trimIndent() else ""
        host.innerHTML = """
            <div style="display:flex; align-items:center; justify-content:space-between; margin-bottom:6px;">
                <h2 style="margin:0; font-size:20px; color:${tm.textPrimary};">
                    ${StringKey.STUDIO_HEADER_TITLE.tr()}$unsavedChip
                </h2>
                <div style="display:flex; align-items:center; gap:12px;">
                    <button id="studioEditProfile" style="
                        background:transparent; border:1px solid ${tm.border};
                        color:${tm.accent}; font-size:12px; font-weight:600;
                        cursor:pointer; padding:6px 12px; border-radius:8px;
                    ">✎ ${StringKey.STUDIO_HEADER_EDIT_PROFILE.tr()}</button>
                    <button id="studioGuide" title="Guide" style="
                        background:transparent; border:1px solid ${tm.border};
                        color:${tm.textSecondary}; width:28px; height:28px;
                        border-radius:50%; cursor:pointer; padding:0;
                        font-family:Georgia,'Times New Roman',serif;
                        font-style:italic; font-size:15px; font-weight:600;
                        line-height:1; display:inline-flex; align-items:center;
                        justify-content:center;
                    ">i</button>
                    <button id="studioClose" style="
                        background:none; border:none; color:${tm.textSecondary};
                        font-size:24px; cursor:pointer; padding:0 4px; line-height:1;
                    ">&times;</button>
                </div>
            </div>
            <div style="color:${tm.textSecondary}; font-size:13px; margin-bottom:4px;
                        display:flex; align-items:center; gap:8px; flex-wrap:wrap;">
                <span>${escapeHtml(workingName)}</span>
                <span>·</span>
                <span>${StringKey.STUDIO_FORM_TARGET_DATE_INLINE.tr()}</span>
                <input id="studio-form-deadline" type="date" value="${isoDate(workingDeadline)}"
                       data-saved="${isoDate(savedGoal.deadline)}"
                       style="padding:3px 6px; border-radius:6px;
                              border:1px solid ${tm.border}; background:${tm.background};
                              color:${tm.textPrimary}; font-size:12px; font-family:inherit;
                              color-scheme: ${if (tm.isDark) "dark" else "light"};"/>
            </div>
        """.trimIndent()
        host.querySelector("#studioClose")?.addEventListener("click", { close() })
        host.querySelector("#studioEditProfile")?.addEventListener("click", { editProfile() })
        host.querySelector("#studioGuide")?.addEventListener("click", {
            // Launch the Studio's own in-modal tour. The anchors live on the
            // section host divs that renderInto wires up (#studioHeader etc).
            GuideOverlay.ensureStylesInstalled()
            GuideOverlay(guide.GuideScreen.STUDIO).start()
        })

        // Wire the deadline date input — moved here from the form so the
        // form's slider rows pack tighter. The horizon slider in the form
        // still drives the date string, and the date string still drives
        // the slider — bidirectional sync.
        val deadlineInput = host.querySelector("#studio-form-deadline") as? HTMLInputElement
        deadlineInput?.addEventListener("change", { _ ->
            val parsed = parseIsoDate(deadlineInput.value) ?: return@addEventListener
            workingDeadline = parsed
            val months = monthsBetween(today, parsed).coerceAtLeast(1)
            sliders = sliders.copy(monthsToGoal = months)
            // Update horizon slider visually.
            val horizonInput = panel.querySelector("#studio-form-horizon") as? HTMLInputElement
            horizonInput?.value = months.toString()
            val display = panel.querySelector("#studio-form-horizon-display") as? HTMLInputElement
            display?.value = formatSliderValue(months.toDouble(), "yearsmonths")
            renderHeader(panel)
            renderStrip(panel)
            renderChart(panel)
            renderSavingsRate(panel)
            renderProgress(panel)
            renderFooter(panel)
        })
    }

    /** Close the Studio and open the onboarding stepper pre-filled with
     *  the current profile, so the user can correct an earlier answer. */
    private fun editProfile() {
        close()
        WebStudioOnboardingComponent(
            apiService = ApiService(),
            initial = profile,
            onComplete = { /* no-op — user can re-open the Studio when ready */ },
        ).show()
    }

    // -----------------------------------------------------------------------
    // Goal form (name + category + target slider + deadline + time-to-goal +
    // monthly contribution + return + future marginal rate)
    // -----------------------------------------------------------------------

    private fun renderGoalForm(panel: HTMLElement) {
        val tm = WebTheme.current
        val host = panel.querySelector("#studioForm") as? HTMLElement ?: return
        val targetMax = kotlin.math.max(50_000.0, savedGoal.targetAmount * 2.0)

        // The Lab is exploration-only (accounts / timelines / scenarios). The
        // goal's category, priority and icon are edited in the simple goal form,
        // not here — but their working values are still carried through to Save
        // via workingGoal() (seeded from the goal at construction).
        host.innerHTML = """
            <div>
                <label style="font-size:12px; color:${tm.textSecondary}; display:block; margin-bottom:4px;">
                    ${StringKey.STUDIO_FORM_GOAL_NAME_LABEL.tr()}
                </label>
                <input id="studio-form-name" type="text" value="${escapeHtml(workingName)}"
                       style="width:100%; box-sizing:border-box;
                              padding:8px 10px; border-radius:8px;
                              border:1px solid ${tm.border}; background:${tm.background};
                              color:${tm.textPrimary}; font-size:14px; font-family:inherit;"/>
            </div>
            <div style="display:grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap:18px; margin-top:14px;">
                ${slider(
                    id = "studio-form-target",
                    label = StringKey.STUDIO_FORM_TARGET_LABEL.tr(),
                    min = 0.0, max = targetMax, step = 100.0,
                    value = workingTargetAmount,
                    savedValue = savedGoal.targetAmount,
                    formatter = "money",
                    tm = tm,
                )}
                ${slider(
                    id = "studio-form-horizon",
                    label = StringKey.STUDIO_LEVER_HORIZON.tr(),
                    min = 1.0, max = 480.0, step = 1.0,
                    value = sliders.monthsToGoal.toDouble(),
                    savedValue = savedSliders.monthsToGoal.toDouble(),
                    formatter = "yearsmonths",
                    tm = tm,
                )}
                ${slider(
                    id = "studio-contribution",
                    label = StringKey.STUDIO_LEVER_CONTRIBUTION.tr(),
                    min = 0.0, max = 5_000.0, step = 1.0,
                    value = sliders.monthlyContribution,
                    savedValue = savedSliders.monthlyContribution,
                    formatter = "money",
                    tm = tm,
                )}
            </div>
            <div style="display:grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap:18px; margin-top:14px;">
                ${slider(
                    id = "studio-return",
                    label = StringKey.STUDIO_LEVER_RETURN.tr(),
                    min = 0.02, max = 0.08, step = 0.005,
                    value = sliders.expectedAnnualReturn,
                    savedValue = savedSliders.expectedAnnualReturn,
                    formatter = "percent",
                    tm = tm,
                )}
                ${slider(
                    id = "studio-future-rate",
                    label = StringKey.STUDIO_LEVER_FUTURE_RATE.tr(),
                    min = 0.15, max = 0.50, step = 0.01,
                    value = sliders.futureMarginalRate ?: ((profile.tax.marginalRateNow - 0.05).coerceAtLeast(0.15)),
                    savedValue = savedSliders.futureMarginalRate ?: ((profile.tax.marginalRateNow - 0.05).coerceAtLeast(0.15)),
                    formatter = "percent",
                    tm = tm,
                )}
            </div>
        """.trimIndent()

        // Wire goal-name input. Header subtitle echoes the working name,
        // so re-render the header on every keystroke so the user sees the
        // change live in two places.
        val nameInput = host.querySelector("#studio-form-name") as? HTMLInputElement
        nameInput?.addEventListener("input", { _ ->
            workingName = nameInput.value
            renderHeader(panel)
            renderFooter(panel)
        })

        // Wire target slider
        wireSlider(panel, "studio-form-target") { v ->
            workingTargetAmount = v
            renderHeader(panel)
            renderStrip(panel)
            renderChart(panel)
            renderSavingsRate(panel)
            renderProgress(panel)
            renderFooter(panel)
        }

        // Wire horizon slider — also drives the deadline input.
        wireSlider(panel, "studio-form-horizon") { v ->
            val months = v.toInt().coerceAtLeast(1)
            sliders = sliders.copy(monthsToGoal = months)
            workingDeadline = today.plus(DatePeriod(months = months))
            // Sync the deadline input directly so the user sees both update.
            val dl = panel.querySelector("#studio-form-deadline") as? HTMLInputElement
            dl?.value = isoDate(workingDeadline)
            renderHeader(panel)
            renderStrip(panel)
            renderChart(panel)
            renderSavingsRate(panel)
            renderProgress(panel)
            renderFooter(panel)
        }

        // (Deadline input lives in the header subtitle now; its change
        // handler is wired in renderHeader.)

        // Wire contribution / return / future-rate sliders.
        wireSlider(panel, "studio-contribution") { v ->
            sliders = sliders.copy(monthlyContribution = v)
            renderHeader(panel)
            renderStrip(panel)
            renderChart(panel)
            renderSavingsRate(panel)
            renderProgress(panel)
            renderFooter(panel)
        }
        wireSlider(panel, "studio-return") { v ->
            sliders = sliders.copy(expectedAnnualReturn = v)
            renderHeader(panel)
            renderStrip(panel)
            renderChart(panel)
            renderSavingsRate(panel)
            renderProgress(panel)
            renderFooter(panel)
        }
        wireSlider(panel, "studio-future-rate") { v ->
            sliders = sliders.copy(futureMarginalRate = v)
            renderHeader(panel)
            renderStrip(panel)
            renderChart(panel)
            renderSavingsRate(panel)
            renderProgress(panel)
            renderFooter(panel)
        }
    }

    // -----------------------------------------------------------------------
    // Vehicle Comparison Strip (compact + checkbox)
    // -----------------------------------------------------------------------

    private fun renderStrip(panel: HTMLElement) {
        val tm = WebTheme.current
        val strip = panel.querySelector("#studioStrip") as? HTMLElement ?: return
        val projections = StudioOrchestrator.runComparison(workingGoal(), profile, sliders, today)
        if (projections.isEmpty()) {
            strip.innerHTML = """
                <div style="
                    flex:1; padding:24px; text-align:center;
                    background:${tm.background}; border-radius:12px;
                    color:${tm.textSecondary}; font-size:13px; line-height:1.5;
                ">
                    ${StringKey.STUDIO_NO_VEHICLES_EMPTY_STATE.tr()}
                </div>
            """.trimIndent()
            return
        }
        strip.innerHTML = projections.joinToString("") { renderVehicleCard(it, tm) }

        // Helper: pin/unpin a vehicle and re-render the dependent UI. Used by
        // both the checkbox change handler and the whole-card click handler.
        // renderChart is required here so the new pinned vehicle's line is
        // brought to the top of the SVG draw order — without it, the z-order
        // only flipped on the next slider change because that path called
        // renderAll. User repro: "the selected vehicle curve shows up on top
        // but ONLY after I play with a slider. […] But that should happen
        // as soon as we click the box."
        fun togglePin(vehicle: VehicleType, makePinned: Boolean) {
            workingPinned = if (makePinned) vehicle else null
            renderHeader(panel)
            renderStrip(panel)
            renderChart(panel)
            renderSavingsRate(panel)
            renderFooter(panel)
        }

        // Wire checkbox-style pin selectors — radio-like across the strip.
        strip.querySelectorAll(".studio-pin-cb").let { nodes ->
            for (i in 0 until nodes.length) {
                val node = nodes.item(i) as? HTMLInputElement ?: continue
                node.addEventListener("change", { _ ->
                    val v = node.getAttribute("data-vehicle") ?: return@addEventListener
                    val vehicle = runCatching { VehicleType.valueOf(v) }.getOrNull() ?: return@addEventListener
                    togglePin(vehicle, node.checked)
                })
            }
        }
        // The whole card body is a click target too — clicking anywhere on a
        // vehicle frame is the same as ticking its checkbox. The card click
        // handler skips when the user actually clicked the checkbox or its
        // label so we don't double-toggle.
        strip.querySelectorAll(".studio-vehicle-card").let { nodes ->
            for (i in 0 until nodes.length) {
                val card = nodes.item(i) as? HTMLElement ?: continue
                card.addEventListener("click", { ev ->
                    val target = ev.target as? org.w3c.dom.Element
                    if (target != null) {
                        val tag = target.tagName.lowercase()
                        if (tag == "input" || tag == "label") return@addEventListener
                    }
                    val v = card.getAttribute("data-vehicle") ?: return@addEventListener
                    if (card.getAttribute("data-eligible") != "true") return@addEventListener
                    val vehicle = runCatching { VehicleType.valueOf(v) }.getOrNull() ?: return@addEventListener
                    val currentlyPinned = workingPinned == vehicle
                    togglePin(vehicle, !currentlyPinned)
                })
            }
        }
    }

    private fun renderVehicleCard(p: VehicleProjection, tm: theme.ThemeColors): String {
        val name = vehicleName(p.vehicle)
        val accent = vehicleAccent(p.vehicle, tm)
        val badge = badgeFor(p.recommendation, tm)
        val isPinned = workingPinned == p.vehicle
        val opacity = if (p.eligible) "1.0" else "0.55"
        val finalAmount = formatMoney(p.finalNetToUser)
        val disabledAttr = if (!p.eligible) "disabled" else ""
        val checkedAttr = if (isPinned) "checked" else ""
        // Bottom line: prefer the inflation note for HISA; otherwise the
        // eligibility-reason text (when ineligible).
        val bottomLine = when {
            p.vehicle == VehicleType.HISA -> StringKey.STUDIO_VEHICLE_HISA_INFLATION_NOTE.tr()
            !p.eligible && p.eligibilityReason != null -> escapeHtml(p.eligibilityReason ?: "")
            else -> ""
        }
        val bottomLineHtml = if (bottomLine.isNotEmpty()) """
            <div style="font-size:11px; color:${tm.textSecondary}; font-style:italic; line-height:1.3; margin-top:6px;">
                $bottomLine
            </div>
        """.trimIndent() else ""
        val cardCursor = if (p.eligible) "pointer" else "not-allowed"
        return """
            <div class="studio-vehicle-card"
                 data-vehicle="${p.vehicle.name}"
                 data-eligible="${p.eligible}"
                 style="
                flex: 1 1 0; min-width:200px;
                padding:12px; border-radius:12px;
                background:${tm.background};
                border:${if (isPinned) "2" else "1"}px solid ${if (isPinned) accent else tm.border};
                opacity:$opacity;
                display:flex; flex-direction:column; gap:6px;
                cursor:$cardCursor;
            ">
                <div style="display:flex; align-items:center; gap:8px;">
                    <input type="checkbox" class="studio-pin-cb"
                           id="pin-${p.vehicle.name}"
                           data-vehicle="${p.vehicle.name}"
                           $checkedAttr $disabledAttr
                           style="accent-color:${accent}; width:16px; height:16px; cursor:$cardCursor;"/>
                    <label for="pin-${p.vehicle.name}" style="
                        font-size:14px; font-weight:600; color:${tm.textPrimary};
                        cursor:${if (p.eligible) "pointer" else "not-allowed"};
                    ">$name</label>
                    <div style="margin-left:auto;">$badge</div>
                </div>
                <div style="display:flex; align-items:baseline; gap:8px;">
                    <div style="font-size:20px; font-weight:700; color:${tm.textPrimary};">
                        ${'$'}$finalAmount
                    </div>
                    <div style="font-size:11px; color:${tm.textSecondary};">
                        ${StringKey.STUDIO_NET_AT_GOAL.tr()}
                    </div>
                </div>
                $bottomLineHtml
            </div>
        """.trimIndent()
    }

    private fun vehicleName(v: VehicleType): String = when (v) {
        VehicleType.TFSA -> StringKey.STUDIO_VEHICLE_TFSA.tr()
        VehicleType.RRSP -> StringKey.STUDIO_VEHICLE_RRSP.tr()
        VehicleType.FHSA -> StringKey.STUDIO_VEHICLE_FHSA.tr()
        VehicleType.RESP -> StringKey.STUDIO_VEHICLE_RESP.tr()
        VehicleType.RDSP -> StringKey.STUDIO_VEHICLE_RDSP.tr()
        VehicleType.NON_REGISTERED -> StringKey.STUDIO_VEHICLE_NON_REGISTERED.tr()
        VehicleType.HISA -> StringKey.STUDIO_VEHICLE_HISA.tr()
        else -> v.name
    }

    private fun vehicleAccent(v: VehicleType, tm: theme.ThemeColors): String = when (v) {
        VehicleType.TFSA -> tm.accent
        VehicleType.RRSP -> "#4A90E2"
        VehicleType.FHSA -> "#F5A623"
        VehicleType.RESP -> tm.incomeGreen
        VehicleType.RDSP -> "#9B59B6"
        VehicleType.HISA -> "#9E9E9E"
        else -> tm.textSecondary
    }

    private fun badgeFor(r: Recommendation, tm: theme.ThemeColors): String {
        val pair: Pair<String, String> = when (r) {
            Recommendation.BEST_FIT -> Pair(StringKey.STUDIO_BADGE_BEST_FIT.tr(), tm.incomeGreen)
            Recommendation.BACKUP -> Pair(StringKey.STUDIO_BADGE_BACKUP.tr(), tm.textSecondary)
            Recommendation.NOT_IDEAL -> Pair(StringKey.STUDIO_BADGE_NOT_IDEAL.tr(), tm.expenseRed)
            Recommendation.INELIGIBLE -> Pair(StringKey.STUDIO_BADGE_INELIGIBLE.tr(), tm.textSecondary)
        }
        val label = pair.first
        val color = pair.second
        return """
            <span style="
                display:inline-block;
                padding:3px 8px; border-radius:999px;
                background:${color}22; color:$color;
                font-size:10px; font-weight:600;
            ">$label</span>
        """.trimIndent()
    }

    // -----------------------------------------------------------------------
    // Slider primitive (now with saved-value tick mark)
    // -----------------------------------------------------------------------

    private fun slider(
        id: String,
        label: String,
        min: Double,
        max: Double,
        step: Double,
        value: Double,
        savedValue: Double?,
        formatter: String,
        tm: theme.ThemeColors,
        extraBelow: String = "",
    ): String {
        val display = formatSliderValue(value, formatter)
        val tickPct = if (savedValue != null && max > min)
            ((savedValue - min) / (max - min) * 100.0).coerceIn(0.0, 100.0)
        else null
        // Tick alignment math: <input type="range"> has a ~16px-wide thumb.
        // At value=min the thumb's CENTER sits 8px from the track's left
        // edge (not at 0% of the wrapper); at value=max the center sits
        // 8px from the right edge. So a tick at percent X needs an extra
        // (8 - X * 0.16)px shift to land where the thumb's center actually
        // is at value X. This makes the tick disappear under the thumb when
        // the slider is at its saved value.
        val tickHtml = if (tickPct != null) {
            val pxShift = 8.0 - (tickPct * 0.16)
            val sign = if (pxShift >= 0) "+" else "-"
            val pxAbs = kotlin.math.abs(pxShift)
            """
            <div style="
                position:absolute; left: calc(${tickPct}% $sign ${pxAbs}px);
                top: 0; bottom: 0; width: 2px;
                background: ${tm.textSecondary};
                opacity: 0.6; pointer-events: none;
                transform: translateX(-1px);
            "></div>
            """.trimIndent()
        } else ""
        return """
            <div>
                <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:6px; gap:8px;">
                    <label style="font-size:12px; color:${tm.textSecondary};">$label</label>
                    <input id="$id-display" type="text" value="$display"
                           data-min="$min" data-max="$max" data-step="$step"
                           style="font-size:13px; font-weight:600; color:${tm.textPrimary};
                                  background:transparent; border:1px solid transparent;
                                  border-radius:6px; padding:2px 6px; min-width:60px;
                                  text-align:right; cursor:text; font-family:inherit;
                                  outline:none; transition:border-color 0.15s;"
                           onfocus="this.style.borderColor='${tm.accent}'; this.select();"
                           onblur="this.style.borderColor='transparent'"/>
                </div>
                <div style="position:relative; padding:8px 0;">
                    $tickHtml
                    <input type="range" id="$id" min="$min" max="$max" step="$step" value="$value"
                           data-formatter="$formatter"
                           style="width:100%; accent-color:${tm.accent}; position:relative; z-index:1;"/>
                </div>
                $extraBelow
            </div>
        """.trimIndent()
    }

    private fun wireSlider(panel: HTMLElement, id: String, update: (Double) -> Unit) {
        val input = panel.querySelector("#$id") as? HTMLInputElement ?: return
        val display = panel.querySelector("#$id-display") as? HTMLInputElement
        val formatter = input.getAttribute("data-formatter") ?: "money"
        input.addEventListener("input", { _ ->
            val v = input.value.toDoubleOrNull() ?: return@addEventListener
            update(v)
            display?.value = formatSliderValue(v, formatter)
        })
        display?.addEventListener("keydown", { evt ->
            val key = (evt as? org.w3c.dom.events.KeyboardEvent)?.key
            if (key == "Enter") {
                evt.preventDefault()
                display.blur()
            } else if (key == "Escape") {
                display.value = formatSliderValue(input.value.toDoubleOrNull() ?: 0.0, formatter)
                display.blur()
            }
        })
        display?.addEventListener("blur", { _ ->
            val parsed = parseSliderValue(display.value, formatter)
            if (parsed != null) {
                val min = input.getAttribute("min")?.toDoubleOrNull() ?: 0.0
                val max = input.getAttribute("max")?.toDoubleOrNull() ?: 0.0
                val step = input.getAttribute("step")?.toDoubleOrNull() ?: 1.0
                val clamped = parsed.coerceIn(min, max)
                val snapped = if (step > 0) (kotlin.math.round(clamped / step) * step) else clamped
                input.value = snapped.toString()
                update(snapped)
            }
            display.value = formatSliderValue(input.value.toDoubleOrNull() ?: 0.0, formatter)
        })
    }

    private fun formatSliderValue(v: Double, formatter: String): String = when (formatter) {
        "money" -> "\$${v.toLong()}"
        "percent" -> "${(v * 1000).toLong() / 10.0}%"
        "yearsmonths" -> formatMonthsAsYearsMonths(v.toInt())
        else -> v.toString()
    }

    private fun formatMonthsAsYearsMonths(months: Int): String {
        val y = months / 12
        val m = months % 12
        return when {
            y > 0 && m > 0 -> "${y}y ${m}m"
            y > 0 -> "${y}y"
            else -> "${m}m"
        }
    }

    private fun parseSliderValue(text: String, formatter: String): Double? {
        val trimmed = text.trim()
        return when (formatter) {
            "money" -> trimmed.removePrefix("$").replace(",", "").trim().toDoubleOrNull()
            "percent" -> {
                val n = trimmed.removeSuffix("%").trim().toDoubleOrNull() ?: return null
                n / 100.0
            }
            "yearsmonths" -> parseYearsMonths(trimmed)
            else -> trimmed.toDoubleOrNull()
        }
    }

    private fun parseYearsMonths(text: String): Double? {
        val s = text.lowercase().replace(" ", "")
        if (s.isEmpty()) return null
        var months = 0.0
        var matched = false
        val yIdx = s.indexOf('y')
        if (yIdx >= 0) {
            val yPart = s.substring(0, yIdx).toDoubleOrNull() ?: return null
            months += yPart * 12.0
            matched = true
            val rest = s.substring(yIdx + 1)
            if (rest.isNotEmpty()) {
                val mIdx = rest.indexOf('m')
                val mStr = if (mIdx >= 0) rest.substring(0, mIdx) else rest
                val mPart = mStr.toDoubleOrNull() ?: return months
                months += mPart
            }
        } else {
            val mIdx = s.indexOf('m')
            if (mIdx >= 0) {
                val mPart = s.substring(0, mIdx).toDoubleOrNull() ?: return null
                months += mPart
                matched = true
            } else {
                val raw = s.toDoubleOrNull() ?: return null
                months = raw * 12.0
                matched = true
            }
        }
        return if (matched) months else null
    }

    // Was a correct-but-separate implementation of whole months — it did the
    // day-of-month adjustment the goal card's version forgot. Correct and duplicated
    // is how drift starts, so it now calls the one definition.
    private fun monthsBetween(from: LocalDate, to: LocalDate): Int =
        analytics.goalMonthsBetween(from, to)

    private fun isoDate(d: LocalDate): String =
        "${d.year}-${d.monthNumber.toString().padStart(2, '0')}-${d.dayOfMonth.toString().padStart(2, '0')}"

    private fun parseIsoDate(s: String): LocalDate? {
        val parts = s.split("-")
        if (parts.size != 3) return null
        val y = parts[0].toIntOrNull() ?: return null
        val m = parts[1].toIntOrNull() ?: return null
        val d = parts[2].toIntOrNull() ?: return null
        return runCatching { LocalDate(y, m, d) }.getOrNull()
    }

    // -----------------------------------------------------------------------
    // Growth Comparison Chart (§5.2)
    // -----------------------------------------------------------------------

    private fun renderChart(panel: HTMLElement) {
        val tm = WebTheme.current
        val host = panel.querySelector("#studioChart") as? HTMLElement ?: return
        val projections = StudioOrchestrator.runComparison(workingGoal(), profile, sliders, today)
            .filter { it.eligible && it.points.isNotEmpty() }
        if (projections.isEmpty()) {
            host.innerHTML = ""
            return
        }

        // SVG scales to fill its container via width:100%. On narrow viewports
        // (mobile), the 840-wide viewBox shrinks to ~360px, so a font-size of 11
        // renders at ~4.7px — unreadable. Bump SVG-unit font sizes and bottom
        // padding when the viewport is narrow; desktop stays exactly as before.
        val isMobile = kotlinx.browser.window.innerWidth < 720
        val width = 840
        val height = if (isMobile) 320 else 280
        val padL = 56
        val padR = 16
        val padT = 16
        val padB = if (isMobile) 56 else 32
        val xTickFontSize = if (isMobile) 22 else 11
        val yTickFontSize = if (isMobile) 22 else 12
        val xTickBaseline = if (isMobile) 28 else 18
        val plotW = width - padL - padR
        val plotH = height - padT - padB

        targetAxisMax = computeAxisCeiling(projections, sliders.monthsToGoal)
        if (animatedAxisMax == 0.0) animatedAxisMax = targetAxisMax
        val maxY = animatedAxisMax
        val totalMonths = projections.maxOf { it.points.size }

        fun xFor(monthIdx: Int): Double = padL + (monthIdx.toDouble() / (totalMonths - 1).coerceAtLeast(1)) * plotW
        fun yFor(amount: Double): Double = padT + plotH - (amount / maxY) * plotH

        val tickValues = generateNiceTicks(targetAxisMax, 5)
        val gridlines = buildString {
            for (tickValue in tickValues) {
                val y = yFor(tickValue)
                val opacity = when {
                    y > padT + plotH -> 0.0
                    y < padT - plotH * 0.25 -> 0.0
                    y < padT -> ((padT - y) / (plotH * 0.25)).let { (1.0 - it).coerceIn(0.0, 1.0) }
                    else -> 1.0
                }
                if (opacity <= 0.001) continue
                append("""<line x1="$padL" y1="$y" x2="${padL + plotW}" y2="$y" stroke="${tm.border}" stroke-width="1" stroke-dasharray="2,3" opacity="$opacity"/>""")
                append("""<text x="${padL - 8}" y="${y + 4}" font-size="$yTickFontSize" font-weight="600" fill="${tm.textPrimary}" text-anchor="end" opacity="$opacity">${'$'}${formatShort(tickValue)}</text>""")
            }
        }

        val years = (totalMonths / 12.0).toInt().coerceAtLeast(1)
        val xTicks = buildString {
            for (i in 0..years) {
                val x = xFor((i * 12).coerceAtMost(totalMonths - 1))
                append("""<text x="$x" y="${padT + plotH + xTickBaseline}" font-size="$xTickFontSize" fill="${tm.textSecondary}" text-anchor="middle">+${i}y</text>""")
            }
        }

        var anyHasDashed = false
        // Z-order — draw the user's selected vehicle LAST so its solid +
        // dashed lines and event dots sit on top when curves overlap. Pre-
        // selected order is irrelevant — what matters is that workingPinned
        // wins the final paint. Mirrors the iOS + Android draw order.
        val orderedForDraw = projections.filter { it.vehicle != workingPinned } +
            projections.filter { it.vehicle == workingPinned }
        // Captured during the loop's pinned-vehicle iteration so the white
        // spotlight dot uses the EXACT (x, y) of the dashed line's last
        // polyline point — no parallel math, no possible drift.
        var spotlightX: Double? = null
        var spotlightY: Double? = null
        val lines = buildString {
            for (p in orderedForDraw) {
                val accent = vehicleAccent(p.vehicle, tm)
                val grossPts = p.points.mapIndexed { i, pt -> "${xFor(i)},${yFor(pt.grossBalance)}" }.joinToString(" ")
                append("""<polyline points="$grossPts" fill="none" stroke="$accent" stroke-width="2.5"/>""")
                val hasRefunds = p.points.any { it.totalWealth > it.grossBalance + 1.0 }
                val lastTotal = p.points.last().totalWealth
                val lastNet = p.points.last().netToUser
                val hasWithdrawalTaxDrop = kotlin.math.abs(lastTotal - lastNet) > 1.0
                // pendingRefund = card's finalNetToUser MINUS what the
                // points timeline ended at. Represents the FHSA/RRSP
                // contribution-year refund that arrives AFTER the goal
                // date but is included in the card's "Net at goal date".
                // Before this fix the chart silently dropped it, so the
                // dashed-line endpoint disagreed with the card by the
                // pending-refund amount (e.g. FHSA card $7,647 vs chart
                // dashed endpoint $7,286 — $361 gap).
                val pendingRefund = p.finalNetToUser - lastNet
                val hasPendingRefund = pendingRefund > 1.0
                if (hasRefunds || hasWithdrawalTaxDrop || hasPendingRefund) {
                    anyHasDashed = true
                    val dashedPts = mutableListOf<String>()
                    var prevCumulative = 0.0
                    for ((i, pt) in p.points.withIndex()) {
                        val currentCumulative = pt.totalWealth - pt.grossBalance
                        if (i > 0 && currentCumulative > prevCumulative + 1.0) {
                            dashedPts += "${xFor(i)},${yFor(pt.grossBalance + prevCumulative)}"
                        }
                        dashedPts += "${xFor(i)},${yFor(pt.totalWealth)}"
                        prevCumulative = currentCumulative
                    }
                    if (hasWithdrawalTaxDrop) {
                        val lastIdx = p.points.size - 1
                        dashedPts += "${xFor(lastIdx)},${yFor(lastNet)}"
                    }
                    // Extend the dashed line up to finalNetToUser at the
                    // goal-date x-position so the dashed endpoint visually
                    // matches the vehicle card's "Net at goal date".
                    if (hasPendingRefund) {
                        val lastIdx = p.points.size - 1
                        dashedPts += "${xFor(lastIdx)},${yFor(p.finalNetToUser)}"
                    }
                    // If this is the pinned vehicle, capture the literal
                    // (x, y) of the FINAL dashed point so the white
                    // spotlight below sits exactly there — no parallel
                    // math, no chance of drift.
                    if (p.vehicle == workingPinned) {
                        val parts = dashedPts.last().split(",")
                        spotlightX = parts[0].toDoubleOrNull()
                        spotlightY = parts[1].toDoubleOrNull()
                    }
                    append("""<polyline points="${dashedPts.joinToString(" ")}" fill="none" stroke="$accent" stroke-width="2" stroke-dasharray="6,4" opacity="0.85"/>""")

                    // Event dots — suppressed for the pinned vehicle.
                    // They cluster near the spotlight at the endpoint
                    // (refund event dots at totalWealth, just below
                    // finalNetToUser) and read as "the line points
                    // there, not at the white dot." Hiding them on
                    // the pinned vehicle keeps the endpoint focus
                    // clean. Tooltips on those events still exist on
                    // OTHER vehicles' lines for context.
                    if (p.vehicle != workingPinned) {
                        for (event in p.events) {
                            val idx = p.points.indexOfFirst { it.date >= event.date }
                                .let { if (it < 0) p.points.size - 1 else it }
                            val cx = xFor(idx)
                            val cy = when (event.type) {
                                VehicleEventType.TAX_OWED -> yFor(p.points[idx].netToUser)
                                else -> yFor(p.points[idx].totalWealth)
                            }
                            val tip = escapeHtml("${event.label}${if (event.explanation != null) " — ${event.explanation}" else ""}")
                            append("""<circle cx="$cx" cy="$cy" r="4" fill="$accent" stroke="${tm.background}" stroke-width="1" data-tip="$tip" style="cursor:help"/>""")
                            append("""<circle cx="$cx" cy="$cy" r="10" fill="$accent" opacity="0" data-tip="$tip" style="cursor:help"/>""")
                        }
                    }
                }
                // Gross-balance endpoint dot — small circle at the
                // solid-line endpoint. Suppressed for the PINNED vehicle
                // because the white spotlight dot below already marks
                // the (more meaningful) finalNetToUser endpoint, and
                // having both at the same X but different Y reads as
                // "shifted markers" instead of "one focused marker".
                if (p.vehicle != workingPinned) {
                    val lastX = xFor(p.points.size - 1)
                    val lastY = yFor(p.points.last().grossBalance)
                    append("""<circle cx="$lastX" cy="$lastY" r="4" fill="$accent"/>""")
                } else if (spotlightX == null) {
                    // Pinned vehicle has no dashed line (HISA-style:
                    // no refunds, no tax drop, no pending refund).
                    // Capture the solid line's endpoint for the
                    // spotlight instead.
                    spotlightX = xFor(p.points.size - 1)
                    spotlightY = yFor(p.points.last().grossBalance)
                }
            }
            // Pinned-vehicle endpoint annotations:
            //   - NET spotlight (white dot + label) at the dashed line's
            //     final point (finalNetToUser). Always shown.
            //   - GROSS spotlight (accent dot + label) at the solid
            //     line's endpoint (grossBalance). Only shown when it
            //     DIVERGES from the net — for FHSA/RRSP that gap is the
            //     tax-refund benefit, the whole reason those vehicles
            //     beat HISA/TFSA. For HISA/TFSA the two values are
            //     identical so the second annotation is redundant.
            //
            // Both dollar labels use a stroke halo for readability;
            // positions are offset so the labels don't collide when the
            // gap is small.
            val pinned = projections.firstOrNull { it.vehicle == workingPinned }
            val sx = spotlightX
            val sy = spotlightY
            if (pinned != null && sx != null && sy != null) {
                val accent = vehicleAccent(pinned.vehicle, tm)
                val grossX = xFor(pinned.points.size - 1)
                val grossY = yFor(pinned.points.last().grossBalance)
                val grossDivergesFromNet = kotlin.math.abs(pinned.finalNetToUser - pinned.points.last().grossBalance) > 1.0

                // NET label sits ABOVE the spotlight; GROSS label sits
                // BELOW its dot (or above, if the gross dot is at the
                // bottom of the chart and there's no room below).
                val netLabel = "$${formatMoney(pinned.finalNetToUser)}"
                val grossLabel = "$${formatMoney(pinned.points.last().grossBalance)}"

                // NET first (will paint above any potential gross-label
                // overlap since SVG paint order = document order).
                append(
                    """<circle cx="$sx" cy="$sy" r="6" fill="white" stroke="$accent" stroke-width="2" pointer-events="none"/>""",
                )
                append(
                    """<text x="$sx" y="${sy - 8}" font-size="15" font-weight="800" fill="white" text-anchor="end" stroke="${tm.background}" stroke-width="3" paint-order="stroke" pointer-events="none">$netLabel</text>""",
                )

                if (grossDivergesFromNet) {
                    // Gross dot: smaller, vehicle-accent fill (no white
                    // center) — reads as the "secondary" value while
                    // the white spotlight reads as the primary.
                    append(
                        """<circle cx="$grossX" cy="$grossY" r="5" fill="$accent" stroke="${tm.background}" stroke-width="2" pointer-events="none"/>""",
                    )
                    // Gross label below the dot, slightly smaller font
                    // than the net label so the hierarchy is clear.
                    append(
                        """<text x="$grossX" y="${grossY + 18}" font-size="12" font-weight="700" fill="$accent" text-anchor="end" stroke="${tm.background}" stroke-width="3" paint-order="stroke" pointer-events="none">$grossLabel</text>""",
                    )
                }
            }
        }

        val legend = projections.joinToString("") { p ->
            val accent = vehicleAccent(p.vehicle, tm)
            val gross = p.points.last().grossBalance
            // Use finalNetToUser (matches the vehicle-card value at the
            // top of the Studio) rather than points.last().netToUser
            // (which omits the pending refund and contradicts the card).
            val net = p.finalNetToUser
            val diverges = kotlin.math.abs(net - gross) > 1.0
            // "Net" value gets the vehicle's accent color (not the
            // muted textSecondary) and full size, so the divergence
            // between in-account and net-at-goal-date is easy to
            // catch on camera. User feedback: "the net value in
            // capture is not easily visible".
            val extra = if (diverges) """
                <span style="color:$accent; font-size:14px; font-weight:700; margin-left:6px;">
                    / ${'$'}${formatMoney(net)} ${StringKey.STUDIO_CHART_NET.tr()}
                </span>
            """.trimIndent() else ""
            """
                <div style="display:inline-flex; align-items:baseline; gap:8px; margin-right:18px;">
                    <span style="width:10px; height:10px; background:$accent; border-radius:2px; display:inline-block;"></span>
                    <span style="color:${tm.textPrimary}; font-size:13px; font-weight:600;">${vehicleName(p.vehicle)}</span>
                    <span style="color:${tm.textPrimary}; font-size:18px; font-weight:700;">${'$'}${formatMoney(gross)}</span>
                    $extra
                </div>
            """.trimIndent()
        }

        val refundCaption = if (anyHasDashed) """
            <div style="margin-top:8px; font-size:11px; color:${tm.textSecondary};">
                ${StringKey.STUDIO_CHART_DASHED_NOTE.tr()}
            </div>
        """.trimIndent() else ""

        val roomCharts = renderRoomMiniCharts(projections, totalMonths, padL, padR, plotW, tm)

        host.innerHTML = """
            <div style="background:${tm.background}; border-radius:12px; padding:16px; position:relative;">
                <div style="display:flex; justify-content:space-between; align-items:baseline; margin-bottom:8px;">
                    <div style="font-size:13px; font-weight:600; color:${tm.textPrimary};">
                        ${StringKey.STUDIO_CHART_TITLE.tr()}
                    </div>
                    <div style="font-size:11px; color:${tm.textSecondary};">
                        ${StringKey.STUDIO_CHART_LEGEND_NET_HINT.tr()}
                    </div>
                </div>
                <svg viewBox="0 0 $width $height" preserveAspectRatio="xMidYMid meet" style="width:100%; height:auto; display:block;">
                    $gridlines
                    $xTicks
                    $lines
                </svg>
                <div class="studio-chart-tip" style="position:absolute; padding:6px 10px; background:${tm.card}; color:${tm.textPrimary}; border:1px solid ${tm.border}; border-radius:6px; font-size:12px; pointer-events:none; opacity:0; transition:opacity 0.12s ease-out; z-index:5; max-width:240px; line-height:1.35; box-shadow:0 4px 12px rgba(0,0,0,0.25);"></div>
                <div style="margin-top:10px; display:flex; flex-wrap:wrap;">$legend</div>
                $refundCaption
                <div style="margin-top:6px; font-size:11px; color:${tm.textSecondary}; opacity:0.8;">
                    ${StringKey.STUDIO_OTHER_GOALS_NOTE.tr()}
                </div>
                $roomCharts
            </div>
        """.trimIndent()
        bindChartTooltips(host)

        if (!animationActive &&
            kotlin.math.abs(targetAxisMax - animatedAxisMax) > targetAxisMax * 0.005
        ) {
            startAxisAnimation(panel)
        }
    }

    /** Wire up the custom 500-ms-delayed tooltip. Each chart marker with a
     *  `data-tip` attribute reveals the `.studio-chart-tip` div after the
     *  cursor lingers for ~500 ms; tip hides instantly on pointerleave. We
     *  re-bind after every innerHTML rewrite (axis animation thrashes this
     *  every frame) — listeners die with the elements they were attached
     *  to, so no leak. */
    private fun bindChartTooltips(host: HTMLElement) {
        val tip = host.querySelector(".studio-chart-tip") as? HTMLElement ?: return
        val markers = host.querySelectorAll("[data-tip]")
        var pending: Int = 0
        for (i in 0 until markers.length) {
            val el = markers.item(i) as? org.w3c.dom.Element ?: continue
            el.addEventListener("pointerenter", { evt ->
                evt as org.w3c.dom.events.MouseEvent
                if (pending != 0) kotlinx.browser.window.clearTimeout(pending)
                val tipText = el.getAttribute("data-tip") ?: ""
                val mouseX = evt.clientX.toDouble()
                val mouseY = evt.clientY.toDouble()
                pending = kotlinx.browser.window.setTimeout({
                    val hostRect = host.getBoundingClientRect()
                    tip.textContent = tipText
                    // Position tip just above-right of the cursor; clamp into
                    // the chart container so it doesn't clip off the side.
                    val rawX = (mouseX - hostRect.left + 14).coerceIn(8.0, hostRect.width - 250.0)
                    val rawY = (mouseY - hostRect.top - 36).coerceAtLeast(8.0)
                    tip.style.left = "${rawX}px"
                    tip.style.top = "${rawY}px"
                    tip.style.opacity = "1"
                    pending = 0
                }, 500)
            })
            el.addEventListener("pointerleave", {
                if (pending != 0) {
                    kotlinx.browser.window.clearTimeout(pending)
                    pending = 0
                }
                tip.style.opacity = "0"
            })
        }
    }

    private fun startAxisAnimation(panel: HTMLElement) {
        animationActive = true
        fun tick(@Suppress("UNUSED_PARAMETER") timestamp: Double) {
            animatedAxisMax += (targetAxisMax - animatedAxisMax) * 0.08
            renderChart(panel)
            if (kotlin.math.abs(animatedAxisMax - targetAxisMax) > targetAxisMax * 0.002) {
                kotlinx.browser.window.requestAnimationFrame(::tick)
            } else {
                animatedAxisMax = targetAxisMax
                animationActive = false
                renderChart(panel)
            }
        }
        kotlinx.browser.window.requestAnimationFrame(::tick)
    }

    private fun computeAxisCeiling(projections: List<VehicleProjection>, monthsToGoal: Int): Double {
        val actualMax = projections
            .flatMap { it.points.map { p -> kotlin.math.max(p.grossBalance, p.totalWealth) } }
            .maxOrNull()
            ?.coerceAtLeast(1.0)
            ?: 1.0
        return niceCeiling(actualMax * 1.1)
    }

    private val niceMantissas = listOf(1.0, 1.5, 2.0, 2.5, 3.0, 4.0, 5.0, 7.5, 10.0)

    private fun niceCeiling(value: Double): Double {
        if (value <= 0) return 1.0
        val exp = kotlin.math.floor(kotlin.math.log10(value))
        val pow = kotlin.math.exp(exp * kotlin.math.ln(10.0))
        val normalized = value / pow
        val nice = niceMantissas.firstOrNull { it >= normalized } ?: 10.0
        return nice * pow
    }

    private fun generateNiceTicks(maxValue: Double, targetCount: Int = 5): List<Double> {
        if (maxValue <= 0) return listOf(0.0)
        val rawStep = maxValue / targetCount
        val exp = kotlin.math.floor(kotlin.math.log10(rawStep))
        val pow = kotlin.math.exp(exp * kotlin.math.ln(10.0))
        val normalized = rawStep / pow
        val niceStep = (niceMantissas.firstOrNull { it >= normalized } ?: 10.0) * pow
        val ticks = mutableListOf<Double>()
        var t = 0.0
        while (t <= maxValue + niceStep * 0.01) {
            ticks += t
            t += niceStep
        }
        return ticks
    }

    private fun renderRoomMiniCharts(
        projections: List<VehicleProjection>,
        totalMonths: Int,
        padL: Int,
        padR: Int,
        plotW: Int,
        tm: theme.ThemeColors,
    ): String {
        val withRoom = projections.filter { p ->
            p.eligible && p.points.any { it.roomRemaining > 0.0 || it.monthlyContribution > 0.0 } &&
                (p.vehicle == VehicleType.TFSA || p.vehicle == VehicleType.RRSP || p.vehicle == VehicleType.FHSA)
        }
        if (withRoom.isEmpty()) return ""

        val height = 90
        val padT = 8
        val padB = 14
        val plotH = height - padT - padB
        val width = padL + plotW + padR
        val maxRoom = withRoom
            .flatMap { it.points.map { p -> p.roomRemaining } }
            .maxOrNull()
            ?.coerceAtLeast(1.0)
            ?: 1.0

        fun xFor(i: Int): Double =
            padL + (i.toDouble() / (totalMonths - 1).coerceAtLeast(1)) * plotW

        fun yFor(amount: Double): Double =
            padT + plotH - (amount / maxRoom) * plotH

        val series = buildString {
            for (p in withRoom) {
                val accent = vehicleAccent(p.vehicle, tm)
                val pts = p.points
                    .mapIndexed { i, pt -> "${xFor(i)},${yFor(pt.roomRemaining)}" }
                    .joinToString(" ")
                val areaPts = "$padL,${padT + plotH} $pts ${padL + plotW},${padT + plotH}"
                append("""<polygon points="$areaPts" fill="$accent" opacity="0.10"/>""")
                append("""<polyline points="$pts" fill="none" stroke="$accent" stroke-width="1.5" opacity="0.9"/>""")
            }
        }
        val yLabels = """
            <text x="${padL - 4}" y="${padT + 8}" font-size="9" fill="${tm.textSecondary}" text-anchor="end">${'$'}${formatShort(maxRoom)}</text>
            <text x="${padL - 4}" y="${padT + plotH}" font-size="9" fill="${tm.textSecondary}" text-anchor="end">${'$'}0</text>
        """.trimIndent()
        val legend = withRoom.joinToString("") { p ->
            val accent = vehicleAccent(p.vehicle, tm)
            val endRoom = p.points.last().roomRemaining
            """
                <span style="display:inline-flex; align-items:center; gap:6px; margin-right:14px;">
                    <span style="display:inline-block; width:8px; height:8px; background:$accent; border-radius:2px;"></span>
                    <span style="font-size:11px; color:${tm.textPrimary}; font-weight:600;">${vehicleName(p.vehicle)}</span>
                    <span style="font-size:11px; color:${tm.textSecondary};">${StringKey.STUDIO_ROOM_CHART_REMAINING.tr(formatMoney(endRoom))}</span>
                </span>
            """.trimIndent()
        }

        return """
            <div style="margin-top:14px; border-top:1px solid ${tm.border}; padding-top:10px;">
                <div style="font-size:12px; font-weight:600; color:${tm.textPrimary}; margin-bottom:6px;">
                    ${StringKey.STUDIO_ROOM_CHART_TITLE.tr()}
                </div>
                <svg viewBox="0 0 $width $height" preserveAspectRatio="none" style="width:100%; height:${height}px; display:block;">
                    $series
                    $yLabels
                </svg>
                <div style="margin-top:4px; display:flex; flex-wrap:wrap;">$legend</div>
            </div>
        """.trimIndent()
    }

    private fun formatShort(amount: Double): String {
        val abs = kotlin.math.abs(amount)
        return when {
            abs >= 1_000_000 -> "${(amount / 1_000_000.0 * 10).toLong() / 10.0}M"
            abs >= 1_000 -> "${(amount / 1_000.0).toLong()}k"
            else -> amount.toLong().toString()
        }
    }

    // -----------------------------------------------------------------------
    // Savings Rate Impact Strip (§5.4)
    // -----------------------------------------------------------------------

    private fun renderSavingsRate(panel: HTMLElement) {
        val tm = WebTheme.current
        val host = panel.querySelector("#studioSavingsRate") as? HTMLElement ?: return
        val projections = StudioOrchestrator.runComparison(workingGoal(), profile, sliders, today)
            .filter { it.eligible }
        val focus = projections.firstOrNull { it.vehicle == workingPinned }
            ?: projections.firstOrNull { it.recommendation == Recommendation.BEST_FIT }
            ?: projections.firstOrNull()
        if (focus == null) {
            host.innerHTML = ""
            return
        }
        val br = focus.effectiveSavingsRateContribution
        val total = br.total
        val outFrac = if (total > 0) br.outOfPocket / total else 1.0
        val taxFrac = if (total > 0) br.taxShifted / total else 0.0
        host.innerHTML = """
            <div style="background:${tm.background}; border-radius:12px; padding:16px;">
                <div style="display:flex; justify-content:space-between; align-items:baseline; margin-bottom:10px;">
                    <div style="font-size:13px; font-weight:600; color:${tm.textPrimary};">
                        ${StringKey.STUDIO_SAVINGS_RATE_TITLE.tr()}
                    </div>
                    <div style="font-size:11px; color:${tm.textSecondary};">
                        ${vehicleName(focus.vehicle)}
                    </div>
                </div>
                <div style="display:flex; align-items:center; gap:14px;">
                    <div style="font-size:28px; font-weight:700; color:${tm.textPrimary}; min-width:80px;">
                        ${formatPercent(total)}
                    </div>
                    <div style="flex:1;">
                        <div style="display:flex; height:12px; border-radius:6px; overflow:hidden; background:${tm.border};">
                            <div style="width:${(outFrac * 100)}%; background:${tm.accent};"></div>
                            <div style="width:${(taxFrac * 100)}%; background:${tm.incomeGreen};"></div>
                        </div>
                        <div style="margin-top:6px; font-size:11px; color:${tm.textSecondary}; display:flex; gap:14px;">
                            <span><span style="display:inline-block; width:8px; height:8px; background:${tm.accent}; border-radius:2px;"></span>
                                ${formatPercent(br.outOfPocket)} ${StringKey.STUDIO_SAVINGS_RATE_OUT_OF_POCKET.tr()}</span>
                            <span><span style="display:inline-block; width:8px; height:8px; background:${tm.incomeGreen}; border-radius:2px;"></span>
                                ${formatPercent(br.taxShifted)} ${StringKey.STUDIO_SAVINGS_RATE_TAX_SHIFTED.tr()}</span>
                        </div>
                    </div>
                </div>
                <div style="margin-top:10px; font-size:11px; color:${tm.textSecondary}; line-height:1.4; opacity:0.85;">
                    ${StringKey.STUDIO_SAVINGS_RATE_EXPLAINER.tr()}
                </div>
            </div>
        """.trimIndent()
    }

    private fun formatPercent(v: Double): String = "${(v * 1000).toLong() / 10.0}%"

    // -----------------------------------------------------------------------
    // Progress bar (current / target)
    // -----------------------------------------------------------------------

    private fun renderProgress(panel: HTMLElement) {
        val tm = WebTheme.current
        val host = panel.querySelector("#studioProgress") as? HTMLElement ?: return
        val current = savedGoal.currentAmount
        val target = workingTargetAmount.coerceAtLeast(0.0)
        val pct = if (target > 0.0) (current / target * 100.0).coerceIn(0.0, 100.0) else 0.0
        host.innerHTML = """
            <div style="background:${tm.background}; border-radius:12px; padding:14px;">
                <div style="display:flex; justify-content:space-between; align-items:baseline; margin-bottom:8px;">
                    <div style="font-size:13px; font-weight:600; color:${tm.textPrimary};">
                        ${StringKey.STUDIO_PROGRESS_TITLE.tr()}
                    </div>
                    <div style="font-size:12px; color:${tm.textSecondary};">
                        ${'$'}${formatMoney(current)} / ${'$'}${formatMoney(target)}
                    </div>
                </div>
                <div style="display:flex; align-items:center; gap:10px;">
                    <div style="flex:1; height:10px; border-radius:5px; background:${tm.border}; overflow:hidden;">
                        <div style="width:${pct}%; height:100%; background:${tm.accent}; border-radius:5px;"></div>
                    </div>
                    <div style="font-size:12px; font-weight:600; color:${tm.textPrimary}; min-width:42px; text-align:right;">
                        ${(pct * 10).toLong() / 10.0}%
                    </div>
                </div>
            </div>
        """.trimIndent()
    }

    // -----------------------------------------------------------------------
    // Footer (Save / Cancel / Delete)
    // -----------------------------------------------------------------------

    private fun renderFooter(panel: HTMLElement) {
        val tm = WebTheme.current
        val host = panel.querySelector("#studioFooter") as? HTMLElement ?: return
        // In create mode the Save button is always live (we're building a
        // brand-new goal, not diffing against a saved one). In edit mode it
        // lights up only when the working state differs from the saved one.
        val saveLive = isCreating || isDirty()
        val saveBg = if (saveLive) tm.accent else tm.border
        val saveColor = if (saveLive) "white" else tm.textSecondary
        val saveCursor = if (saveLive) "pointer" else "not-allowed"
        // Delete button is meaningless in create mode — the draft goal hasn't
        // been persisted yet, so Cancel already discards it.
        val deleteButton = if (isCreating) "<span></span>" else """
            <button id="studio-delete" style="
                background:transparent; color:${tm.expenseRed};
                border:1px solid ${tm.expenseRed}; border-radius:8px;
                padding:8px 14px; font-size:13px; font-weight:600;
                cursor:pointer; font-family:inherit;
            ">${StringKey.STUDIO_DELETE_GOAL.tr()}</button>
        """.trimIndent()
        host.innerHTML = """
            <div style="display:flex; justify-content:space-between; gap:10px; align-items:center;">
                $deleteButton
                <div style="display:flex; gap:10px;">
                    <button id="studio-cancel"
                        title="${StringKey.STUDIO_CANCEL_TOOLTIP.tr()}"
                        style="
                            background:transparent; color:${tm.textPrimary};
                            border:1px solid ${tm.border}; border-radius:8px;
                            padding:8px 14px; font-size:13px; font-weight:600;
                            cursor:pointer; font-family:inherit;
                        ">${StringKey.STUDIO_CANCEL_ACTION.tr()}</button>
                    <button id="studio-save" style="
                        background:$saveBg; color:$saveColor;
                        border:none; border-radius:8px;
                        padding:8px 18px; font-size:13px; font-weight:600;
                        cursor:$saveCursor; font-family:inherit;
                    ">${saveActionLabel()}</button>
                </div>
            </div>
        """.trimIndent()
        host.querySelector("#studio-save")?.addEventListener("click", {
            if (labApplyToGoal != null) {
                showApplyToGoalConfirm()
            } else if (isCreating || isDirty()) {
                commitAll()
            }
        })
        host.querySelector("#studio-cancel")?.addEventListener("click", {
            // In create mode there's nothing to revert to — the goal was never
            // saved. Just close, discarding the draft.
            if (isCreating) close() else revertAll()
        })
        host.querySelector("#studio-delete")?.addEventListener("click", {
            viewModel.deleteGoal(savedGoal.id)
            close()
        })
    }

    private fun saveActionLabel(): String = when {
        labApplyToGoal != null -> StringKey.LAB_APPLY_TO_GOAL.tr()
        isCreating -> StringKey.LAB_SAVE_AS_GOAL.tr()
        else -> StringKey.STUDIO_SAVE_ACTION.tr()
    }

    private fun fmtMoney(v: Double): String = "$" + kotlin.math.round(v).toInt().toString()
    private fun fmtDate(d: kotlinx.datetime.LocalDate): String =
        "${d.year}-${d.monthNumber.toString().padStart(2, '0')}-${d.dayOfMonth.toString().padStart(2, '0')}"
    private fun acctLabel(a: models.GoalAccountType?): String = a?.name ?: "—"

    /** "Apply to goal" — enumerate every field the Lab can push to the goal
     *  (highlighting the ones that changed, dimming the unchanged), and commit
     *  (updateGoal) only on confirm. Nothing auto-saves. */
    private fun showApplyToGoalConfirm() {
        val orig = labApplyToGoal ?: return
        val tm = WebTheme.current
        // label, from, to, changed — all applicable fields, always listed.
        val fields = listOf(
            listOf(StringKey.LAB_FIELD_TARGET.tr(), fmtMoney(orig.targetAmount), fmtMoney(workingTargetAmount), (kotlin.math.abs(orig.targetAmount - workingTargetAmount) > 0.005).toString()),
            listOf(StringKey.LAB_FIELD_DEADLINE.tr(), fmtDate(orig.deadline), fmtDate(workingDeadline), (orig.deadline != workingDeadline).toString()),
            listOf(StringKey.LAB_FIELD_ACCOUNT.tr(), acctLabel(orig.accountType), acctLabel(workingPinned), (orig.accountType != workingPinned).toString()),
        )
        val anyChanged = fields.any { it[3] == "true" }
        val confirmDisabled = !anyChanged

        val overlay = document.createElement("div") as HTMLElement
        overlay.style.cssText = "position:fixed; inset:0; background:rgba(0,0,0,0.5); z-index:1300; display:flex; align-items:center; justify-content:center;"
        val rowsHtml = fields.joinToString("") { f ->
            val label = f[0]; val from = f[1]; val to = f[2]; val changed = f[3] == "true"
            val valueHtml = if (changed)
                """<span style="color:${tm.textSecondary};">$from</span> <span style="color:${tm.textSecondary};">→</span> <span style="color:${tm.accent}; font-weight:700;">$to</span>"""
            else
                """<span style="color:${tm.textSecondary};">$to</span>"""
            """<div style="display:flex; justify-content:space-between; gap:12px; align-items:center; padding:9px 0; border-bottom:1px solid ${tm.border}; opacity:${if (changed) "1" else "0.55"};">
                <span style="color:${tm.textPrimary}; font-size:0.85rem; font-weight:${if (changed) "600" else "500"};">$label</span>
                <span style="font-size:0.85rem; text-align:right;">$valueHtml</span>
            </div>"""
        }
        val noteHtml = if (confirmDisabled)
            """<div style="color:${tm.textSecondary}; font-size:0.82rem; margin-top:0.75rem;">${StringKey.LAB_APPLY_NO_CHANGES.tr()}</div>""" else ""
        overlay.innerHTML = """
            <div style="background:${tm.card}; border:1px solid ${tm.border}; border-radius:14px; padding:1.5rem; max-width:420px; width:90%;">
                <h3 style="margin:0 0 1rem 0; color:${tm.textPrimary}; font-size:1.1rem;">${StringKey.LAB_APPLY_TITLE.tr()}</h3>
                <div style="margin-bottom:1.25rem;">$rowsHtml$noteHtml</div>
                <div style="display:flex; justify-content:flex-end; gap:10px;">
                    <button id="labApplyCancel" style="background:transparent; color:${tm.textPrimary}; border:1px solid ${tm.border}; border-radius:8px; padding:8px 14px; font-weight:600; cursor:pointer;">${StringKey.ACTION_CANCEL.tr()}</button>
                    <button id="labApplyConfirm" ${if (confirmDisabled) "disabled" else ""} style="background:${if (confirmDisabled) tm.border else tm.accent}; color:${if (confirmDisabled) tm.textSecondary else "white"}; border:none; border-radius:8px; padding:8px 18px; font-weight:600; cursor:${if (confirmDisabled) "not-allowed" else "pointer"};">${StringKey.LAB_APPLY_CONFIRM.tr()}</button>
                </div>
            </div>
        """.trimIndent()
        document.body?.appendChild(overlay)
        val closeOverlay = { overlay.remove() }
        overlay.querySelector("#labApplyCancel")?.addEventListener("click", { closeOverlay() })
        overlay.addEventListener("click", { e -> if (e.target == overlay) closeOverlay() })
        overlay.querySelector("#labApplyConfirm")?.addEventListener("click", {
            if (!confirmDisabled) { commitAll(); closeOverlay(); close() }
        })
    }

    /** Persist all working state via viewModel.updateGoal, then re-sync the
     *  saved snapshot so isDirty() returns false and the (unsaved) chip
     *  disappears. The Studio stays open.
     *
     *  In create mode the same button instead calls viewModel.createGoal and
     *  closes the Studio after a successful save (the draft id was a stub). */
    private fun commitAll() {
        val deadlineJs = kotlin.js.Date(
            year = workingDeadline.year,
            month = workingDeadline.monthNumber - 1,
            day = workingDeadline.dayOfMonth,
        )
        if (isCreating) {
            if (workingName.isBlank()) {
                showAppAlert("Please enter a goal name")
                return
            }
            viewModel.createGoal(
                name = workingName,
                category = workingCategory,
                targetAmount = workingTargetAmount,
                deadline = deadlineJs,
                iconEmoji = workingIcon,
                priority = workingPriority,
                accountType = workingPinned,
                onCreated = { _ -> close() },
            )
            return
        }
        viewModel.updateGoal(
            goal = savedGoal,
            name = workingName,
            targetAmount = workingTargetAmount,
            deadline = deadlineJs,
            category = workingCategory,
            priority = workingPriority,
            iconEmoji = workingIcon,
            accountType = workingPinned,
            // Preserve existing debt fields — let updateGoal's defaults kick
            // in to keep them stable. Explicitly passing avoids null-overwrite.
            debtInterestRate = savedGoal.debtInterestRate,
            debtRemainingBalance = savedGoal.debtRemainingBalance,
            debtRemainingMonths = savedGoal.debtRemainingMonths,
            debtPaymentFrequency = savedGoal.debtPaymentFrequency,
            debtMonthlyEquivalentPayment = savedGoal.debtMonthlyEquivalentPayment,
        )
        // Sync saved snapshot to the just-persisted working state.
        savedGoal = workingGoal()
        savedSliders = sliders
        workingPinned?.let { onPinned(it) }
        currentPanel?.let { renderAll(it) }
    }

    /** Revert working state back to the last-saved snapshot and re-render
     *  every section so the UI reflects the rollback. */
    private fun revertAll() {
        workingName = savedGoal.name
        workingCategory = savedGoal.category
        workingTargetAmount = savedGoal.targetAmount
        workingDeadline = savedGoal.deadline
        workingPinned = savedGoal.accountType
        workingPriority = savedGoal.priority
        workingIcon = iconIdFor(savedGoal.iconEmoji, savedGoal.category)
        sliders = savedSliders
        currentPanel?.let { renderAll(it) }
    }

    // -----------------------------------------------------------------------
    // Helpers
    // -----------------------------------------------------------------------

    private fun formatMoney(amount: Double): String {
        val rounded = kotlin.math.round(kotlin.math.abs(amount)).toLong()
        val withSeparators = rounded.toString().reversed().chunked(3).joinToString(",").reversed()
        return if (amount < 0) "-$withSeparators" else withSeparators
    }

    private fun shortDateLabel(d: LocalDate): String = "${d.year}-${d.monthNumber.toString().padStart(2, '0')}-${d.dayOfMonth.toString().padStart(2, '0')}"

    /** Delegates to the shared [HtmlEscape]; kept as a member so call sites read unchanged. */
    private fun escapeHtml(s: String): String = HtmlEscape.escape(s)
}
